/**
 * sl-lkdb.3.2: Hook to detect PITR recovery and trigger local data clear + re-sync.
 *
 * Checks the server's recoveryVersion against localStorage on mount and again
 * each time the backend becomes reachable again: a browser that was offline
 * during a recovery never received the signal. Also subscribes to the WebSocket recoveryCompleted$ event for real-time
 * notification when an admin triggers recovery while the user is online.
 *
 * When a version mismatch is detected for an existing local cache:
 * 1. Shows a blocking dialog (via returned state)
 * 2. Erases the local data of the workspace (stale Y.js data)
 * 3. Reloads the page to trigger fresh sync from server
 *
 * Several signals can report the same recovery; one run handles them. A page
 * that already started from data cleared for a version and is told again that
 * this version makes it stale does not reload once more: clearing did not
 * work, and the dialog offers to try again.
 */
import { getWorkspaceInfoQuery } from '@affine/graphql';
import { isDebugVerbosityAtLeast } from '@affine/nbstore';
import { useService, useServiceOptional } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBackendAvailable } from '../../../components/hooks/use-backend-availability';
import {
  isBackendUnavailableError,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import { WorkspaceServerService } from '../../cloud';
import type { Workspace } from '../entities/workspace';
import { WorkspacesService } from '../services/workspaces';
import {
  hadLocalWorkspaceDatabasesAtPageStart,
  RECOVERY_VERSION_KEY,
  recoveryVersionAtPageStart,
  shouldRefreshLocalWorkspaceCache,
} from './workspace-recovery-version';

export interface WorkspaceRecoveryState {
  /** True when recovery is in progress (dialog should be shown) */
  isRecovering: boolean;
  /** Current recovery phase for localized user-facing copy */
  phase: WorkspaceRecoveryPhase;
  /** Determinate stage progress, 0..1 */
  progressPercent: number | null;
  /** Why the local data could not be brought up to date; the dialog offers a retry. */
  failure: WorkspaceRecoveryFailure | null;
  /** Retry the recovery process */
  retry: () => void;
}

export type WorkspaceRecoveryFailure =
  /** Clearing the local data threw. */
  | { kind: 'clearFailed'; message: string }
  /** The local data was cleared and reloaded, and is still reported as stale. */
  | { kind: 'stillStale' };

export type WorkspaceRecoveryPhase =
  | 'idle'
  | 'clearingLocalData'
  | 'syncingFreshData'
  | 'reloading';

export function useWorkspaceRecovery(
  workspace: Workspace | null
): WorkspaceRecoveryState {
  const [isRecovering, setIsRecovering] = useState(false);
  const [phase, setPhase] = useState<WorkspaceRecoveryPhase>('idle');
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [failure, setFailure] = useState<WorkspaceRecoveryFailure | null>(null);
  const runningRecovery = useRef(false);
  const requestedVersion = useRef<number | null>(null);
  const hasChecked = useRef(false);
  const [checkRound, setCheckRound] = useState(0);
  const serverService = useServiceOptional(WorkspaceServerService);
  const workspacesService = useService(WorkspacesService);
  const backendAvailable = useBackendAvailable();

  const isLocal = !workspace || workspace.flavour === 'local';
  const workspaceId = workspace?.id ?? '';

  const performRecovery = useCallback(
    async (newVersion: number) => {
      if (!workspace || runningRecovery.current) return;
      runningRecovery.current = true;
      requestedVersion.current = newVersion;
      setIsRecovering(true);
      setFailure(null);
      setProgressPercent(0.2);
      setPhase('clearingLocalData');

      try {
        // Short pause so the progress is seen before the page goes away.
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 1: Erase the local data of this workspace. The page reloads
        // right after it, nothing may write local data in between.
        await workspacesService.eraseLocalData(workspace.meta);

        // Step 2: Update localStorage with new version
        setProgressPercent(0.65);
        setPhase('syncingFreshData');
        localStorage.setItem(
          RECOVERY_VERSION_KEY(workspaceId),
          String(newVersion)
        );

        // Step 3: Reload to trigger fresh sync from server
        setProgressPercent(0.9);
        setPhase('reloading');
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unknown error during recovery';
        console.error('[useWorkspaceRecovery] Recovery failed:', err);
        runningRecovery.current = false;
        setFailure({ kind: 'clearFailed', message });
        setPhase('idle');
        setProgressPercent(null);
      }
    },
    [workspace, workspaceId, workspacesService]
  );

  // A browser that was offline may have missed the recovery signal.
  useEffect(() => {
    if (isLocal) {
      return;
    }
    return onBackendAvailable(() => {
      hasChecked.current = false;
      setCheckRound(round => round + 1);
    });
  }, [isLocal]);

  // Check recovery version on mount and after each reconnect (via GraphQL)
  useEffect(() => {
    if (
      isLocal ||
      hasChecked.current ||
      !backendAvailable ||
      !serverService?.server ||
      !workspaceId
    ) {
      return;
    }
    hasChecked.current = true;

    void (async () => {
      try {
        const result = await serverService.server!.gql({
          query: getWorkspaceInfoQuery,
          variables: { workspaceId },
        });

        const serverVersion = result.workspace.recoveryVersion;
        const localVersionStr = localStorage.getItem(
          RECOVERY_VERSION_KEY(workspaceId)
        );
        const localVersion = localVersionStr
          ? Number.parseInt(localVersionStr, 10)
          : null;
        const hadLocalWorkspaceDatabases =
          await hadLocalWorkspaceDatabasesAtPageStart(workspaceId);

        if (isDebugVerbosityAtLeast(3)) {
          console.log(
            `[useWorkspaceRecovery] Version check: server=${serverVersion}, local=${localVersion ?? 'missing'}, localDbsAtPageStart=${hadLocalWorkspaceDatabases}`
          );
        }

        if (
          shouldRefreshLocalWorkspaceCache({
            serverVersion,
            localVersionStr,
            hadLocalWorkspaceDatabases,
          })
        ) {
          console.warn(
            `[useWorkspaceRecovery] Recovery detected! server=${serverVersion} > local=${localVersion ?? 'missing'} with local workspace data`
          );
          void performRecovery(serverVersion);
        } else {
          // A first visit is not a recovery event: the databases it opened
          // itself hold nothing stale, so it records the current server version.
          if (localVersionStr !== String(serverVersion)) {
            localStorage.setItem(
              RECOVERY_VERSION_KEY(workspaceId),
              String(serverVersion)
            );
          }
        }
      } catch (err) {
        // Non-critical — if the GQL call fails, we just skip the check.
        // The WebSocket subscription below will catch real-time events.
        hasChecked.current = false;
        if (!isBackendUnavailableError(err)) {
          console.warn(
            '[useWorkspaceRecovery] Failed to fetch recovery version:',
            err
          );
        }
      }
    })();
  }, [
    backendAvailable,
    checkRound,
    isLocal,
    serverService,
    workspaceId,
    performRecovery,
  ]);

  // Subscribe to real-time WebSocket recoveryCompleted$ events
  useEffect(() => {
    const recoveryCompleted$ = workspace?.engine.recoveryCompleted$;
    if (!recoveryCompleted$ || isLocal) {
      return;
    }

    const subscription = recoveryCompleted$.subscribe(event => {
      console.warn(
        `[useWorkspaceRecovery] WebSocket recovery event received:`,
        event
      );
      const clearedFor = recoveryVersionAtPageStart(workspaceId);
      if (
        clearedFor !== null &&
        event.newRecoveryVersion <= clearedFor &&
        !runningRecovery.current
      ) {
        requestedVersion.current = event.newRecoveryVersion;
        setIsRecovering(true);
        setFailure({ kind: 'stillStale' });
        setPhase('idle');
        setProgressPercent(null);
        return;
      }
      void performRecovery(event.newRecoveryVersion);
    });

    return () => subscription.unsubscribe();
  }, [workspace, workspaceId, isLocal, performRecovery]);

  const retry = useCallback(() => {
    if (requestedVersion.current === null) return;
    void performRecovery(requestedVersion.current);
  }, [performRecovery]);

  return { isRecovering, phase, progressPercent, failure, retry };
}
